/**
 * SafeImage.jsx — an <img> that never shows the browser's broken-image icon.
 *
 * A post's picture can disappear after it was published: the author deleted
 * the original a quote was taken from, a moderator removed it, or a file never
 * finished uploading. The browser's answer is a torn-page icon in an empty
 * frame, which reads as Clincordant being broken. This shows a quiet "Image
 * unavailable" panel instead, the same size, so the layout does not jump.
 */

import { useState, useEffect } from 'react';
import { ImageOff } from 'lucide-react';

export default function SafeImage({ fallback = 'Image unavailable', className = '', onError, ...img }) {
  const [failed, setFailed] = useState(false);

  // A new picture gets a fresh chance.
  useEffect(() => { setFailed(false); }, [img.src]);

  if (failed) {
    return (
      <span
        className={`img-fallback ${className}`}
        role="img"
        aria-label={img.alt ? `${img.alt} (image unavailable)` : 'Image unavailable'}
      >
        <ImageOff size={18} aria-hidden="true" />
        <span>{fallback}</span>
      </span>
    );
  }

  return (
    <img
      className={className}
      {...img}
      onError={(e) => { setFailed(true); onError?.(e); }}
    />
  );
}
